import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { TreeDeciduous, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './Button';
import { supabase } from '../lib/supabase';
import { updateSectionLocation } from '../lib/updateSectionLocation';
import useSectionLayout from '../hooks/useSectionLayout';
import { cn } from '../utils/cn';

interface LessonsSectionProps {
  open: boolean;
  toggle: (key: string) => void;
  sectionKey?: string;
  showChevron?: boolean;
}

export function LessonsSection({ open, toggle, sectionKey = 'lessons', showChevron = true }: LessonsSectionProps) {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const { locations } = useSectionLayout();

  useEffect(() => {
    loadLessons();
  }, []);

  async function loadLessons() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('lessons_learned')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setLessons(data || []);
    } catch (error) {
      console.error('Error loading lessons:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleLessonsClick() {
    navigate('/journaling-overview');
  }

  const currentLocation = locations?.lessons ?? 'tools';

  const handleToggleLayout = async () => {
    const target = currentLocation === 'tools' ? 'loop' : 'tools';
    setIsToggling(true);
    try {
      await updateSectionLocation('lessons', target);
      window.location.reload();
    } catch (error) {
      console.error('Error updating section location:', error);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => toggle(sectionKey)}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <TreeDeciduous className="w-5 h-5 text-[#004060]" />
          Lessons
        </div>
        {showChevron && (
          <ChevronDown
            className={cn(
              'w-5 h-5 text-[#236caf] transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        )}
      </button>

      {open && (
        <div className="p-4 sm:p-6 space-y-4">
          {loading ? (
            <div className="text-center py-4 text-gray-500">Loading lessons...</div>
          ) : lessons.length > 0 ? (
            <div className="space-y-3">
              {lessons.map((lesson: any) => (
                <div key={lesson.id} className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">
                    {format(new Date(lesson.created_at), 'MMM d, yyyy')}
                  </p>
                  <p className="text-gray-900">{lesson.lesson}</p>
                  {lesson.core_value && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {lesson.core_value}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <TreeDeciduous className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">No lessons learned yet</p>
              <Button onClick={handleLessonsClick} variant="outline" size="sm">
                Add Your First Lesson
              </Button>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button onClick={handleLessonsClick} className="flex-1">
              <Plus className="h-4 w-4 mr-2" />
              Add Lesson
            </Button>
            <Button
              onClick={handleToggleLayout}
              disabled={isToggling}
              variant="outline"
              className="flex-1"
            >
              {isToggling
                ? 'Moving...'
                : `Move to ${currentLocation === 'tools' ? 'Loop' : 'Tools'}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LessonsSection;
